import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import './WetlandForm.css';
import './WetlandDetails.css';

const isFilled = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return false;
    return s.toLowerCase() !== 'nije navedeno';
  }
  if (Array.isArray(v)) {
    return v
      .map(x => (typeof x === 'string' ? x.trim() : x))
      .filter(x =>
        x !== null &&
        x !== undefined &&
        String(x).trim() !== '' &&
        String(x).trim().toLowerCase() !== 'nije navedeno'
      ).length > 0;
  }
  if (typeof v === 'object') {
    return Object.keys(v || {}).length > 0;
  }
  return true;
};

const DetailRow = ({ label, value }) => {
  if (!isFilled(value)) return null;
  return (
    <div className="detail-row">
      <strong className="detail-label">{label}:</strong>
      <span className="detail-value">{value}</span>
    </div>
  );
};

const WetlandDetails = ({ wetland, onClose }) => {
  const { t } = useLanguage();

  const tr = (key) => (isFilled(key) ? t(key) : '');
  const dateLocale = (t.language === 'sr') ? 'sr-Latn' : (t.language || 'sr-Latn');
  const observationDate = new Date(wetland.observationTimestamp).toLocaleString(
    dateLocale,
    { dateStyle: 'long', timeStyle: 'short' }
  );

  const hasWaterDetails =
    isFilled(wetland.waterClarity) ||
    isFilled(wetland.waterColor) ||
    isFilled(wetland.canSeeBottom) ||
    isFilled(wetland.surfaceAlgae) ||
    isFilled(wetland.waterPlants) ||
    isFilled(wetland.surroundingPlants);

  const hasWildlifeSection =
    isFilled(wetland.animalsInOrOnWater) ||
    (Array.isArray(wetland.seenAnimalsList) && isFilled(wetland.seenAnimalsList)) ||
    isFilled(wetland.otherSeenAnimals);

  const hasThreatsSection =
    (Array.isArray(wetland.observedThreatsList) && isFilled(wetland.observedThreatsList)) ||
    isFilled(wetland.otherThreatDescription);

  const hasLocation =
    isFilled(wetland?.location?.latitude) || isFilled(wetland?.location?.longitude);

  return (
    <div className="wetland-form-modal" onClick={onClose}>
      <div className="wetland-form-container" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <h2>{"Detalji lokacije"}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="form-body">
          {/* Informacije o vlažnom staništu */}
          <div className="form-section">
            <h3>{"Informacije o vlažnom staništu"}</h3>
            <DetailRow label={t('Tip')} value={tr(wetland.wetlandType)} />
          </div>

          {/* Slike vlažnog staništa */}
          {wetland.wetlandTypePhotoUrls && wetland.wetlandTypePhotoUrls.length > 0 && (
            <div className="form-section">
              <h3>{"Slike vlažnog staništa"}</h3>
              <div className="photo-preview-grid">
                {wetland.wetlandTypePhotoUrls.map((url, index) => (
                  <div key={index} className="photo-preview-item">
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={`Vlažno stanište ${index + 1}`} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detalji o vodi */}
          {hasWaterDetails && (
            <div className="form-section">
              <h3>{t('waterDetails')}</h3>
              <DetailRow label={t('waterClarity')} value={tr(wetland.waterClarity)} />
              <DetailRow label={t('waterColor')} value={tr(wetland.waterColor)} />
              <DetailRow label={t('canSeeBottom')} value={tr(wetland.canSeeBottom)} />
              <DetailRow label={t('surfaceAlgae')} value={tr(wetland.surfaceAlgae)} />
              <DetailRow label={t('waterPlants')} value={tr(wetland.waterPlants)} />
              <DetailRow label={t('surroundingPlants')} value={tr(wetland.surroundingPlants)} />
            </div>
          )}

          {/* Životinje */}
          {hasWildlifeSection && (
            <div className="form-section">
              <h3>{t('wildlife')}</h3>
              <DetailRow label={t('animalsInOrOnWater')} value={tr(wetland.animalsInOrOnWater)} />

              {Array.isArray(wetland.seenAnimalsList) && isFilled(wetland.seenAnimalsList) && (
                <div className="detail-row">
                  <strong className="detail-label">{t('seenAnimals')}:</strong>
                  <ul className="details-list">
                    {wetland.seenAnimalsList
                      .filter(isFilled)
                      .map((animal) => <li key={animal}>{tr(animal) || animal}</li>)}
                  </ul>
                </div>
              )}

              <DetailRow label={t('otherSeenAnimals')} value={wetland.otherSeenAnimals} />
            </div>
          )}

          {/* Uočene opasnosti (tekstualni deo) */}
          {hasThreatsSection && (
            <div className="form-section">
              <h3>{t('observedThreats')}</h3>

              {Array.isArray(wetland.observedThreatsList) && isFilled(wetland.observedThreatsList) && (
                <div className="detail-row">
                  <strong className="detail-label">{t()}</strong>
                  <ul className="details-list">
                    {wetland.observedThreatsList
                      .filter(isFilled)
                      .map((threat) => <li key={threat}>{tr(threat) || threat}</li>)}
                  </ul>
                </div>
              )}

              <DetailRow
                label={t('otherThreatDescription')}
                value={wetland.otherThreatDescription}
              />
            </div>
          )}

          {/* Fotke uočenih opasnosti – posebna sekcija sa linijom i plavim naslovom */}
          {wetland.observedThreatPhotoUrls &&
            wetland.observedThreatPhotoUrls.length > 0 && (
            <div className="form-section">
              <h3>{"Fotografije uočenih opasnosti"}</h3>
              <div className="photo-preview-grid">
                {wetland.observedThreatPhotoUrls.map((url, index) => (
                  <div key={index} className="photo-preview-item">
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={`Opasnost ${index + 1}`} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lokacija */}
          {hasLocation && (
            <div className="form-section">
              <h3>{"Lokacija"}</h3>
              <DetailRow label={"Geografska širina"} value={wetland.location?.latitude} />
              <DetailRow label={"Geografska dužina"} value={wetland.location?.longitude} />
            </div>
          )}

          {/* Vreme observacije */}
          <div className="form-section">
            <DetailRow label={"Vreme observacije"} value={observationDate} />
          </div>
        </div>

        <div className="form-footer">
          <button
            type="button"
            className="cancel-button"
            onClick={onClose}
            style={{ flex: '1' }}
          >
            {"Zatvori"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WetlandDetails;
